const normalize = (value) => String(value || '').trim().toLowerCase()

export const CHAT_DOCUMENT_EXTENSIONS = [
    '.pdf',
    '.doc', '.docx', '.rtf', '.odt',
    '.xls', '.xlsx', '.xlsm', '.xlsb', '.ods', '.csv', '.tsv',
    '.txt', '.md', '.markdown', '.json', '.xml', '.log', '.ini', '.cfg', '.yaml', '.yml',
    '.ppt', '.pptx', '.odp',
    '.mxl', '.epf', '.erf', '.dt',
    '.zip', '.rar', '.7z',
]

export const CHAT_DOCUMENT_ACCEPT = CHAT_DOCUMENT_EXTENSIONS.join(',')

const getExtension = (value = '') => {
    const clean = normalize(value).split(/[?#]/)[0]
    const match = clean.match(/\.([^.\\/]+)$/)
    return match ? `.${match[1]}` : ''
}

const IMAGE_EXTS = new Set(['.png', '.jpg', '.jpeg', '.gif', '.webp', '.bmp', '.avif', '.svg'])
const VIDEO_EXTS = new Set(['.mp4', '.mov', '.m4v', '.webm', '.mkv', '.avi'])
const AUDIO_EXTS = new Set(['.mp3', '.wav', '.m4a', '.aac', '.ogg', '.oga', '.webm'])
const SPREADSHEET_EXTS = new Set(['.xls', '.xlsx', '.xlsm', '.xlsb', '.ods', '.csv', '.tsv'])
const TEXT_EXTS = new Set(['.txt', '.md', '.markdown', '.json', '.xml', '.log', '.ini', '.cfg', '.yaml', '.yml'])
const WORD_EXTS = new Set(['.doc', '.docx', '.rtf', '.odt'])
const PRESENTATION_EXTS = new Set(['.ppt', '.pptx', '.odp'])
const ARCHIVE_EXTS = new Set(['.zip', '.rar', '.7z'])
const ONE_C_EXTS = new Set(['.mxl', '.epf', '.erf', '.dt'])

const MIME_BY_EXT = Object.freeze({
    '.jpg': 'image/jpeg',
    '.jpeg': 'image/jpeg',
    '.png': 'image/png',
    '.webp': 'image/webp',
    '.gif': 'image/gif',
    '.bmp': 'image/bmp',
    '.svg': 'image/svg+xml',
    '.avif': 'image/avif',

    '.mp4': 'video/mp4',
    '.mov': 'video/quicktime',
    '.m4v': 'video/x-m4v',
    '.webm': 'video/webm',
    '.mkv': 'video/x-matroska',
    '.avi': 'video/x-msvideo',

    '.mp3': 'audio/mpeg',
    '.wav': 'audio/wav',
    '.m4a': 'audio/mp4',
    '.aac': 'audio/aac',
    '.ogg': 'audio/ogg',
    '.oga': 'audio/ogg',

    '.pdf': 'application/pdf',
    '.txt': 'text/plain',
    '.md': 'text/markdown',
    '.markdown': 'text/markdown',
    '.json': 'application/json',
    '.xml': 'application/xml',
    '.log': 'text/plain',
    '.ini': 'text/plain',
    '.cfg': 'text/plain',
    '.yaml': 'application/yaml',
    '.yml': 'application/yaml',

    '.csv': 'text/csv',
    '.tsv': 'text/tab-separated-values',
    '.xls': 'application/vnd.ms-excel',
    '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    '.xlsm': 'application/vnd.ms-excel.sheet.macroEnabled.12',
    '.xlsb': 'application/vnd.ms-excel.sheet.binary.macroEnabled.12',
    '.ods': 'application/vnd.oasis.opendocument.spreadsheet',

    '.doc': 'application/msword',
    '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    '.rtf': 'application/rtf',
    '.odt': 'application/vnd.oasis.opendocument.text',

    '.ppt': 'application/vnd.ms-powerpoint',
    '.pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    '.odp': 'application/vnd.oasis.opendocument.presentation',

    '.zip': 'application/zip',
    '.rar': 'application/vnd.rar',
    '.7z': 'application/x-7z-compressed',

    // У MXL/EPF/ERF/DT нет универсального web MIME, поэтому содержимое
    // остаётся бинарным, а вид файла определяется по исходному имени.
    '.mxl': 'application/octet-stream',
    '.epf': 'application/octet-stream',
    '.erf': 'application/octet-stream',
    '.dt': 'application/octet-stream',
})

export const inferMediaMimeType = ({ mimeType = '', name = '', source = '' } = {}) => {
    const rawMime = normalize(mimeType).split(';')[0]
    const ext = getExtension(name) || getExtension(source)

    if (rawMime && rawMime !== 'application/octet-stream' && rawMime !== 'binary/octet-stream') {
        return rawMime
    }

    return MIME_BY_EXT[ext] || rawMime || 'application/octet-stream'
}

export const detectMediaDocumentKind = ({ mimeType = '', name = '', source = '', mediaType = '' } = {}) => {
    const mime = inferMediaMimeType({ mimeType, name, source })
    const explicit = normalize(mediaType)
    const ext = getExtension(name) || getExtension(source)

    if (explicit === 'image' || mime.startsWith('image/')) return 'image'
    if (mime === 'application/pdf') return 'pdf'
    if (explicit === 'video' || mime.startsWith('video/')) return 'video'
    if (explicit === 'voice' || explicit === 'audio' || mime.startsWith('audio/')) return 'audio'

    if (IMAGE_EXTS.has(ext)) return 'image'
    if (ext === '.pdf') return 'pdf'
    if (VIDEO_EXTS.has(ext)) return 'video'
    if (AUDIO_EXTS.has(ext)) return 'audio'

    if (
        SPREADSHEET_EXTS.has(ext) ||
        mime.includes('spreadsheet') ||
        mime.includes('ms-excel') ||
        mime.includes('opendocument.spreadsheet') ||
        mime.includes('sheet.macroenabled') ||
        mime.includes('sheet.binary.macroenabled') ||
        mime === 'text/csv' ||
        mime === 'text/tab-separated-values'
    ) return 'spreadsheet'

    if (
        TEXT_EXTS.has(ext) ||
        mime.startsWith('text/') ||
        mime === 'application/json' ||
        mime === 'application/xml' ||
        mime === 'application/yaml' ||
        mime === 'text/xml'
    ) return 'text'

    if (
        WORD_EXTS.has(ext) ||
        mime.includes('wordprocessingml') ||
        mime === 'application/msword' ||
        mime.includes('opendocument.text') ||
        mime === 'application/rtf'
    ) return 'word'

    if (
        PRESENTATION_EXTS.has(ext) ||
        mime.includes('presentationml') ||
        mime.includes('ms-powerpoint') ||
        mime.includes('opendocument.presentation')
    ) return 'presentation'

    if (ONE_C_EXTS.has(ext)) return 'one-c'
    if (ARCHIVE_EXTS.has(ext) || mime.includes('zip') || mime.includes('rar') || mime.includes('7z')) return 'archive'

    return explicit === 'model' ? 'model' : 'file'
}

export const getViewerActionLabel = ({ mimeType = '', name = '', source = '', mediaType = '' } = {}) => {
    const kind = detectMediaDocumentKind({ mimeType, name, source, mediaType })

    switch (kind) {
        case 'video': return 'Запустить'
        case 'audio': return 'Прослушать'
        case 'spreadsheet': return 'Открыть таблицу'
        case 'pdf': return 'Открыть PDF'
        case 'text': return 'Открыть'
        case 'image': return 'Открыть'
        case 'one-c': return 'Подробнее'
        case 'word':
        case 'presentation': return 'Открыть файл'
        default: return 'Открыть'
    }
}

export const isSpreadsheetFile = (value = {}) => detectMediaDocumentKind(value) === 'spreadsheet'
export const isTextDocumentFile = (value = {}) => detectMediaDocumentKind(value) === 'text'

export const getDocumentKindLabel = (kind) => {
    switch (kind) {
        case 'image': return 'Изображение'
        case 'video': return 'Видео'
        case 'audio': return 'Аудио'
        case 'pdf': return 'PDF'
        case 'spreadsheet': return 'Таблица'
        case 'text': return 'Текстовый файл'
        case 'word': return 'Документ Word'
        case 'presentation': return 'Презентация'
        case 'one-c': return 'Файл 1С'
        case 'archive': return 'Архив'
        case 'model': return '3D-модель'
        default: return 'Файл'
    }
}
